import { WEEKLY_SCHEDULE, SCHEDULE_HIGHLIGHTS } from "../lib/academy-data";
import SectionHeader from "../components/SectionHeader";
import { Sun, Moon, Info } from "lucide-react";

const DAILY_SAMPLE = [
  { time: "06:00", text: "השכמה" },
  { time: "06:15", text: "פריסה קלה" },
  { time: "06:30", text: "אימון בוקר" },
  { time: "07:30", text: "מקלחת, ארוחת בוקר, התארגנות ללימודים" },
  { time: "08:15", text: "תחילת יום הלימודים" },
  { time: "14:00", text: "סיום יום הלימודים + ארוחת צהריים" },
  { time: "14:30", text: "מנוחת צהריים" },
  { time: "16:00", text: "זמן לימודים + פריסת צהריים" },
  { time: "18:00", text: "יציאה לאימון" },
  { time: "21:00", text: "חזרה מאימון + ארוחת ערב" },
];

export default function Schedule() {
  return (
    <div data-testid="schedule-page">
      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-[#0A0A0A]">
        <div className="absolute inset-0 diagonal-stripe opacity-40" />
        <div className="container mx-auto px-6 md:px-10 relative">
          <p className="font-heading text-xs md:text-sm font-bold tracking-[0.3em] text-[#DC2626] uppercase mb-4">
            לו״ז שבועי
          </p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight leading-[0.9] max-w-4xl">
            השבוע שלך
            <br />
            <span className="text-[#DC2626]">כשחקן אקדמיה.</span>
          </h1>
          <p className="font-body text-lg md:text-xl text-neutral-300 mt-6 leading-relaxed max-w-3xl">
            לו״ז שבועי מקצועי המשלב 3 אימוני בוקר, 5 אימוני קבוצה, ואיזון בין עומסים והתאוששות —
            בדיוק כמו שחקני קבוצה בוגרת.
          </p>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-12 bg-[#050505] border-y border-white/10">
        <div className="container mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SCHEDULE_HIGHLIGHTS.map((h, i) => (
              <div
                key={h.title}
                className="bg-[#171717] border border-white/10 p-6"
                data-testid={`schedule-highlight-${i}`}
              >
                <div className="font-heading text-2xl font-black text-[#DC2626] mb-2 uppercase">
                  {h.title}
                </div>
                <p className="font-body text-sm text-neutral-300 leading-relaxed">
                  {h.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Weekly Table */}
      <section className="py-20 md:py-24 bg-[#0A0A0A]">
        <div className="container mx-auto px-6 md:px-10">
          <SectionHeader
            kicker="לוח אימונים"
            title="לו״ז שבועי לדוגמה"
            description="שובץ בצורה שמאזנת בין עומסים ומאפשרת התאוששות בין ימי שיא — אימוני בוקר בשני, שלישי וחמישי, ויום עמוס ברביעי (Double session)."
            testId="schedule-table-header"
          />

          <div className="overflow-x-auto">
            <table className="w-full text-right border-collapse min-w-[700px]" data-testid="weekly-table">
              <thead>
                <tr>
                  <th className="bg-[#DC2626] text-white font-heading font-black p-4 md:p-5 text-right uppercase tracking-wider text-sm md:text-base border-l border-white/20">
                    יום
                  </th>
                  <th className="bg-[#DC2626] text-white font-heading font-black p-4 md:p-5 text-right uppercase tracking-wider text-sm md:text-base border-l border-white/20">
                    <span className="inline-flex items-center gap-2">
                      <Sun size={18} strokeWidth={2.5} />
                      בוקר
                    </span>
                  </th>
                  <th className="bg-[#DC2626] text-white font-heading font-black p-4 md:p-5 text-right uppercase tracking-wider text-sm md:text-base">
                    <span className="inline-flex items-center gap-2">
                      <Moon size={18} strokeWidth={2.5} />
                      אחר הצהריים / ערב
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {WEEKLY_SCHEDULE.map((row, i) => (
                  <tr
                    key={row.day}
                    className={i % 2 === 0 ? "bg-[#171717]" : "bg-[#121212]"}
                    data-testid={`schedule-row-${i}`}
                  >
                    <td className="p-4 md:p-5 border-b border-white/5 text-white font-heading font-bold text-base md:text-lg">
                      {row.day}
                    </td>
                    <td className="p-4 md:p-5 border-b border-white/5 text-neutral-200 font-body text-sm md:text-base">
                      {row.morning}
                    </td>
                    <td className="p-4 md:p-5 border-b border-white/5 text-neutral-200 font-body text-sm md:text-base">
                      {row.evening}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Notes */}
          <div className="mt-8 bg-[#171717] border border-white/10 border-r-4 border-r-[#DC2626] p-6">
            <div className="flex items-start gap-3">
              <Info size={22} strokeWidth={2.5} className="text-[#DC2626] flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-heading font-bold text-white mb-2">איך נבנה הלו״ז</h4>
                <ul className="font-body text-sm md:text-base text-neutral-300 space-y-2 leading-relaxed">
                  <li>
                    <strong className="text-white">חלוקת אימוני הבוקר:</strong> שני, שלישי וחמישי —
                    רצף עבודה בתחילת השבוע, כשיום חמישי מוקדש לליטוש טכני לפני סוף השבוע.
                  </li>
                  <li>
                    <strong className="text-white">שילוב אימוני הכוח:</strong> אימון בוקר ביום שני
                    פותח את השבוע עם דגש על כוח מתפרץ. אימון אחר הצהריים ברביעי משולב לפני אימון
                    הקבוצה — יום "עמוס" שמדמה עצימות גבוהה של שחקן מקצועני.
                  </li>
                  <li>
                    <strong className="text-white">אימוני כדורסל בבוקר:</strong> בימים שלישי וחמישי —
                    התמקדות בשיפור יכולות אישיות (Skill Development) וקליעה, בנפרד מהאימון הקבוצתי הטקטי.
                  </li>
                  <li>
                    <strong className="text-white">אימוני קבוצה:</strong> 5 אימונים פרוסים על ימי
                    א'-ה', מה שמשאיר את סוף השבוע למשחקים או להתאוששות מלאה.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Daily sample */}
      <section className="py-20 md:py-24 bg-[#050505]">
        <div className="container mx-auto px-6 md:px-10">
          <SectionHeader
            kicker="לו״ז יומי"
            title="יום באקדמיה"
            description="כך נראה יום שבו מתקיים אימון בוקר — שילוב מאוזן בין אימון, לימודים ומנוחה."
            testId="daily-schedule-header"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="daily-schedule">
            {DAILY_SAMPLE.map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-5 bg-[#171717] border border-white/10 hover:border-[#DC2626]/40 p-5 transition-colors"
                data-testid={`daily-item-${i}`}
              >
                <div className="font-heading text-xl md:text-2xl font-black text-[#DC2626] min-w-[64px]" dir="ltr">
                  {item.time}
                </div>
                <div className="w-px h-10 bg-white/10" />
                <p className="font-body text-base text-white">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
